import type {
  GetTermByIdentifierRequest,
  GetTermByIdentifierResponse,
  IncludeAcceptanceRequest,
  IncludeAcceptanceResponse,
  ListTermsByIdentifierRequest,
  ListTermsByIdentifierResponse,
  RemoveAcceptanceRequest,
  RemoveAcceptanceResponse,
  VerifyAcceptanceRequest,
  VerifyAcceptanceResponse,
} from '../../types/drf/drfType';
import { DREF_ROUTES } from '../requestsService';

export class DrfService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Checks whether a person has accepted a specific term.
   * Endpoint: POST /v1/bff/dref/verificar-aceite
   * When acceptance already exists, the API may return `{ termo: null }` or a sparse
   * `termo` object with only `dataTermoAceite` populated.
   */
  async verifyAcceptance(request: VerifyAcceptanceRequest): Promise<VerifyAcceptanceResponse> {
    return this.fetch<VerifyAcceptanceResponse>(DREF_ROUTES.POST_VERIFY_ACCEPTANCE, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Removes (revokes) a term acceptance.
   * Endpoint: POST /v1/bff/dref/remover-aceite
   */
  async removeAcceptance(request: RemoveAcceptanceRequest): Promise<RemoveAcceptanceResponse> {
    return this.fetch<RemoveAcceptanceResponse>(DREF_ROUTES.POST_REMOVE_ACCEPTANCE, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * Retrieves term details by association identifier and version.
   * Endpoint: POST /v1/bff/dref/recuperar-termo-por-identificador
   */
  async getTermByIdentifier(
    request: GetTermByIdentifierRequest,
  ): Promise<GetTermByIdentifierResponse> {
    return this.fetch<GetTermByIdentifierResponse>(
      DREF_ROUTES.POST_RECOVER_TERM_BY_IDENTIFIER,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Lists terms by association identifier with pagination.
   * Endpoint: POST /v1/bff/dref/listar-termos-por-identificador
   */
  async listTermsByIdentifier(
    request: ListTermsByIdentifierRequest,
  ): Promise<ListTermsByIdentifierResponse> {
    return this.fetch<ListTermsByIdentifierResponse>(
      DREF_ROUTES.POST_LIST_TERMS_BY_IDENTIFIER,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Records a person's acceptance of a term.
   * Endpoint: POST /v1/bff/dref/incluir-aceite
   */
  async includeAcceptance(request: IncludeAcceptanceRequest): Promise<IncludeAcceptanceResponse> {
    return this.fetch<IncludeAcceptanceResponse>(DREF_ROUTES.POST_INCLUDE_ACCEPTANCE, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}
